import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import classes from './styles.module.scss'
import { createCn } from '../../helpers/utils';
import { useTranslation } from 'react-i18next';
import { Chevron, GreenTick, Users } from '../../assets';
import { toast } from 'react-toastify';
import FileChat from './components/chat.component';
import ShareMeetingModal from './components/share-meeting-modal.component';
import ProtocolView from './components/protocol-view.component';
import SummaryView from './components/summary-view.component';
import TranscriptionView from './components/transcription-view.component';
import SpeakerLanguagePanel from './components/speaker-language-panel.component';
import TranscriptionImprovePanel from './components/transcription-improve-panel.component';
import TeamsImportPanel from './components/teams-import-panel.component';
import MeetingAnalyticsTab from './components/meeting-analytics-tab.component';
import MeetingVideoInsightsTab from './components/meeting-video-insights-tab.component';
import MeetingTasksPanel from './components/meeting-tasks-panel.component';
import MeetingSectionEditToolbar from './components/meeting-section-edit-toolbar.component';
import TranscriptionMediaPanel from './components/transcription-media-panel.component';
import { meetingHasVideoFile } from './utils/meeting-media';
import { formatTaskDeadline, DEFAULT_TASK_DEADLINE } from '../../utils/task-deadline';
import { apiService, KeywordTheme, MeetingInsights, normalizeSegments, PresentationDeck, SlideDeckSummary, SlideTimelineEntry, SpeechSegment, StructuredTask, SummaryStructured, VideoAnalysisMeta, VideoScreenInsights } from '../../services/api.service';
import {
  formatSecondsShort,
  formatSpeakerShortName,
  formatSpeakerFullName,
  stripSpeakerOrgSuffix,
  getSegmentEndSec,
  getSegmentStartSec,
  getSpeakerName,
  parseTimeToSeconds,
  speakerNamesMatch,
  SPEAKER_COLORS,
  sumSegmentDurationsSec,
  unionSpeechDurationSec,
} from './utils/audio-segments';
import { resolveSpeakerRole, ROLE_SORT_ORDER } from '../../utils/speaker-roles';
import { resolveSpeakerLang } from './utils/speaker-language';
import {
  computeTranscriptionImproveStats,
  getTranscriptionImproveMode,
} from './utils/transcription-improve';
import { markdownToHtml } from '../../utils/markdown';
import {
  downloadAsDoc,
  downloadBlob,
  segmentsToHtml,
  summaryToHtml,
  tasksToHtml,
} from '../../utils/download-document';
import { parseProtocolMarkdown } from '../../utils/parse-protocol';
import { sanitizeExportModelBranding } from '../../utils/protocol-footer';
import { buildExportFilename } from '../../utils/export-filename';
import { videoScreenInsightsToHtml } from './utils/video-insights-export';
import { useMeetingAutoRefresh } from '../../hooks/use-meeting-auto-refresh.hook';
import { useMeetingProgress } from '../../hooks/use-meeting-progress.hook';
import { useCleanTranscript } from '../../hooks/use-clean-transcript.hook';
import ProcessingPanel from './components/processing-panel.component';
import type {
  ConversationMetrics,
  EmotionAnalysis,
  FillerStats,
  MeetingDetails,
  ProsodyAnalysis,
} from '../../services/api.service';

const cn = createCn('file', classes)

const baseTabs = ['protocol', 'summary', 'tasks', 'transcription', 'analytics'] as const;
type BaseFileTab = typeof baseTabs[number];
type FileTab = BaseFileTab | 'video_insights';
const editableTabs: FileTab[] = ['protocol', 'summary', 'tasks'];

function capitalizeTask(text: string): string {
  const t = text.trim();
  if (!t) return t;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function resolveMeetingTasks(
  protocol: string,
  structuredTasks: StructuredTask[],
  summaryStructured: SummaryStructured | null,
): StructuredTask[] {
  if (structuredTasks.length) {
    return structuredTasks.map(t => ({
      ...t,
      task: capitalizeTask(t.task),
      assignee: t.assignee?.trim() || '—',
      deadline: formatTaskDeadline(t.deadline),
    }));
  }
  if (protocol?.trim()) {
    const parsed = parseProtocolMarkdown(protocol);
    if (parsed.tasks.length) {
      return parsed.tasks.map(t => ({
        task: capitalizeTask(t.task),
        assignee: t.assignee?.trim() || '—',
        deadline: formatTaskDeadline(t.deadline?.trim()),
        priority: t.priority?.trim() || 'Средний',
      }));
    }
  }
  const items = summaryStructured?.action_items || [];
  return items.map(raw => {
    const text = String(raw || '').trim();
    const dash = text.indexOf(' — ');
    if (dash > 0) {
      return {
        task: capitalizeTask(text.slice(0, dash).trim()),
        assignee: text.slice(dash + 3).trim() || '—',
        deadline: DEFAULT_TASK_DEADLINE,
        priority: 'Средний',
      };
    }
    return { task: capitalizeTask(text), assignee: '—', deadline: DEFAULT_TASK_DEADLINE, priority: 'Средний' };
  }).filter(t => t.task);
}

function formatMeetingDate(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const FilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { state } = useLocation();

  const [tab, setTab] = useState<FileTab>('protocol');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState(state?.file?.name || '');
  const [meetingStatus, setMeetingStatus] = useState('');
  const [duration, setDuration] = useState(state?.file?.duration || '0:00:00');
  const [isVideoMeeting, setIsVideoMeeting] = useState(false);
  const [videoAnalysis, setVideoAnalysis] = useState<VideoAnalysisMeta | null>(null);
  const [protocol, setProtocol] = useState('');
  const [summary, setSummary] = useState('');
  const [summaryStructured, setSummaryStructured] = useState<SummaryStructured | null>(null);
  const [structuredTasks, setStructuredTasks] = useState<StructuredTask[]>([]);
  const [transcription, setTranscription] = useState('');
  const [segments, setSegments] = useState<SpeechSegment[]>([]);
  const [audioUrl, setAudioUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingPipeline, setMeetingPipeline] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingSummary, setRegeneratingSummary] = useState(false);
  const [regenMessage, setRegenMessage] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [insightsPptxLoading, setInsightsPptxLoading] = useState(false);
  const [savingToDb, setSavingToDb] = useState(false);
  const [sectionEditing, setSectionEditing] = useState(false);
  const [savingSection, setSavingSection] = useState(false);
  const [editDraftProtocol, setEditDraftProtocol] = useState('');
  const [editDraftSummary, setEditDraftSummary] = useState('');
  const [editDraftTasks, setEditDraftTasks] = useState<StructuredTask[]>([]);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [audioDurationSec, setAudioDurationSec] = useState(0);
  const [insights, setInsights] = useState<MeetingInsights | null>(null);
  const [slideTimeline, setSlideTimeline] = useState<SlideTimelineEntry[]>([]);
  const [presentationSlides, setPresentationSlides] = useState<SlideTimelineEntry[]>([]);
  const [presentationDecks, setPresentationDecks] = useState<PresentationDeck[]>([]);
  const [slideDeckSummary, setSlideDeckSummary] = useState<SlideDeckSummary | null>(null);
  const [videoScreenInsights, setVideoScreenInsights] = useState<VideoScreenInsights | null>(null);
  const [summarizingSlides, setSummarizingSlides] = useState(false);
  const [slidesVlmAnalyzing, setSlidesVlmAnalyzing] = useState(false);
  const [generatingPptx, setGeneratingPptx] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [transcriptSearchQuery, setTranscriptSearchQuery] = useState('');
  const [transcriptSearchTerms, setTranscriptSearchTerms] = useState<string[]>([]);
  const [speakerLanguages, setSpeakerLanguages] = useState<Record<string, string>>({});
  const [speakerGenders, setSpeakerGenders] = useState<Record<string, { gender?: string; display?: string }>>({});
  const [savingSpeakerLangs, setSavingSpeakerLangs] = useState(false);
  const [retranscribing, setRetranscribing] = useState(false);
  const [retranscribeWithProtocol, setRetranscribeWithProtocol] = useState(false);
  const [rediarizing, setRediarizing] = useState(false);
  const [reprocessingDeepgram, setReprocessingDeepgram] = useState(false);
  const [soloSpeakerName, setSoloSpeakerName] = useState<string | null>(null);
  const [fillingGaps, setFillingGaps] = useState(false);
  const [detectingLangs, setDetectingLangs] = useState(false);
  const [importingTeams, setImportingTeams] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // Задача F1: голосовая аналитика (H1 filler_stats, B1 conversation_metrics, B2 emotion/prosody).
  const [conversationMetrics, setConversationMetrics] = useState<ConversationMetrics | null>(null);
  const [fillerStats, setFillerStats] = useState<FillerStats | null>(null);
  const [emotionAnalysis, setEmotionAnalysis] = useState<EmotionAnalysis | null>(null);
  const [prosodyAnalysis, setProsodyAnalysis] = useState<ProsodyAnalysis | null>(null);
  const [recomputingAnalytics, setRecomputingAnalytics] = useState(false);
  const autoDetectRanRef = useRef(false);
  const seekAudioRef = useRef<((time: number) => void) | null>(null);
  const togglePlayPauseRef = useRef<(() => void) | null>(null);
  const kzPollOnLoadRef = useRef(false);
  const protocolRegenOnLoadRef = useRef(false);

  const applySegmentsResponse = useCallback((segsResponse: Awaited<ReturnType<typeof apiService.getMeetingSegments>>) => {
    setSegments(normalizeSegments(segsResponse));
    if (segsResponse.speaker_languages) {
      setSpeakerLanguages(segsResponse.speaker_languages);
    }
    if (segsResponse.speaker_genders) {
      setSpeakerGenders(segsResponse.speaker_genders);
    }
    if (segsResponse.slide_timeline?.length) {
      setSlideTimeline(segsResponse.slide_timeline);
    }
    if (segsResponse.presentation_slides?.length) {
      setPresentationSlides(segsResponse.presentation_slides);
    }
    if (segsResponse.presentation_decks?.length) {
      setPresentationDecks(segsResponse.presentation_decks);
    }
  }, []);

  const pollKazakhRetranscribe = useCallback(async () => {
    if (!id) return;
    setRetranscribing(true);
    toast.info('Обработка казахских реплик (перевод KZ→RU, 1–3 мин)…');
    const delays = [30000, 30000, 45000, 60000, 90000];
    for (const delay of delays) {
      await new Promise(resolve => setTimeout(resolve, delay));
      try {
        const segsResponse = await apiService.getMeetingSegments(id);
        applySegmentsResponse(segsResponse);
        if (!segsResponse.kazakh_retranscribe_pending) {
          toast.success('RU-перевод казахских реплик обновлён');
          setRetranscribing(false);
          return;
        }
      } catch {
        // keep polling
      }
    }
    toast.info('Обработка ещё идёт — обновите страницу позже');
    setRetranscribing(false);
  }, [id, applySegmentsResponse]);

  const applyMeetingDetails = useCallback((details: MeetingDetails) => {
    setName(details.name);
    setMeetingStatus(details.status || '');
    setMeetingDate(details.date || '');
    setMeetingPipeline(details.pipeline || null);
    setDuration(details.duration || '0:00:00');
    setIsVideoMeeting(
      details.pipeline === 'video'
      || !!details.has_img
      || /\.(mp4|webm|mov|mkv|avi|m4v)$/i.test(details.file_path || ''),
    );
    setVideoAnalysis(details.video_analysis || null);
    setProtocol(details.protocol || details.transcript || '');
    setTranscription(details.transcription || '');
    if (meetingHasVideoFile(details)) {
      void apiService.checkMeetingVideoAvailable(details.id).then((available) => {
        setVideoUrl(available ? apiService.getMeetingVideoUrl(details.id) : '');
      });
    } else {
      setVideoUrl('');
    }
    setAudioUrl(apiService.getMeetingAudioUrl(details.id));
    setSummary(details.summary || '');
    setSummaryStructured(details.summary_structured || null);
    setStructuredTasks(details.structured_tasks || []);
    setSpeakerLanguages(details.speaker_languages || {});
    const cached = details.insights;
    if (
      cached &&
      (
        cached.key_topics?.length ||
        cached.key_moments?.length ||
        cached.keywords?.length ||
        cached.themes?.length ||
        cached.action_items?.length ||
        cached.speaker_tones?.length ||
        cached.tone_segments?.length
      )
    ) {
      setInsights(cached);
    }
    if (details.slide_timeline?.length) {
      setSlideTimeline(details.slide_timeline);
    }
    if (details.presentation_slides?.length) {
      setPresentationSlides(details.presentation_slides);
    }
    if (details.presentation_decks?.length) {
      setPresentationDecks(details.presentation_decks);
    }
    if (details.slide_deck_summary) {
      setSlideDeckSummary(details.slide_deck_summary);
    }
    setVideoScreenInsights(details.video_screen_insights || null);
    const vaStatus = details.video_analysis?.status;
    setSlidesVlmAnalyzing(vaStatus === 'generating');
    setConversationMetrics(details.conversation_metrics || null);
    setFillerStats(details.filler_stats || null);
    setEmotionAnalysis(details.emotion_analysis || null);
    setProsodyAnalysis(details.prosody_analysis || null);
  }, []);

  const handleRecomputeAnalytics = useCallback(async () => {
    if (!id || recomputingAnalytics) return;
    setRecomputingAnalytics(true);
    try {
      await apiService.recomputeMeetingAnalytics(id, true);
      const details = await apiService.getMeetingDetails(id);
      applyMeetingDetails(details);
      toast.success('Аналитика пересчитана');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось пересчитать аналитику');
    } finally {
      setRecomputingAnalytics(false);
    }
  }, [id, recomputingAnalytics, applyMeetingDetails]);

  const refreshMeetingData = useCallback(async () => {
    if (!id || sectionEditing) return;
    const details = await apiService.getMeetingDetails(id);
    applyMeetingDetails(details);
    try {
      const segsResponse = await apiService.getMeetingSegments(id);
      applySegmentsResponse(segsResponse);
      if (segsResponse.kazakh_retranscribe_pending) {
        kzPollOnLoadRef.current = true;
      }
    } catch {
      // segments optional
    }
  }, [id, sectionEditing, applyMeetingDetails, applySegmentsResponse]);

  const handleAutoSyncCompleted = useCallback(() => {
    toast.success(t('file.auto-sync-ready'));
  }, [t]);

  const { liveProgress, isLiveSyncing } = useMeetingAutoRefresh({
    meetingId: id,
    enabled: meetingStatus === 'processing' && !loading,
    sectionEditing,
    onSync: refreshMeetingData,
    onCompleted: handleAutoSyncCompleted,
  });

  // Задача L1: отдельный от useMeetingAutoRefresh поллинг — тот отвечает за детект
  // завершения и релоад данных, этот только питает панель обработки (степпер/ETA).
  const { progress: liveProcessing } = useMeetingProgress(
    id,
    meetingStatus === 'processing' && !loading && !sectionEditing,
  );

  // Задача H1: переключатель «Без слов-паразитов» в транскрипте (свои сегменты, не влияет
  // на segments из основного стейта — используются аналитикой/плеером/таймлайнами).
  const cleanTranscript = useCleanTranscript(id, segments);

  const goBack = () => {
    navigate(state?.returnUrl || '/files');
  }

  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const details = await apiService.getMeetingDetails(id);
        if (cancelled) return;

        applyMeetingDetails(details);
        setAudioUrl(apiService.getMeetingAudioUrl(id));

        let summaryText = details.summary || '';
        if (!summaryText) {
          try {
            const generated = await apiService.generateMeetingSummary(id);
            summaryText = generated.summary || '';
          } catch {
            // summary optional
          }
        }
        if (!cancelled) {
          if (summaryText) {
            setSummary(summaryText);
          }
        }

        try {
          const segsResponse = await apiService.getMeetingSegments(id);
          if (!cancelled) {
            applySegmentsResponse(segsResponse);
            if (segsResponse.kazakh_retranscribe_pending) {
              kzPollOnLoadRef.current = true;
            }
          }
        } catch {
          // segments optional for timeline
        }

        try {
          const regen = await apiService.getRegenerateStatus(id);
          if (!cancelled && regen.status === 'generating') {
            if (regen.protocol) {
              setProtocol(regen.protocol);
            }
            setRegenerating(true);
            setRegenMessage(regen.message || 'Генерация протокола…');
            protocolRegenOnLoadRef.current = true;
          }
        } catch {
          // regen status optional
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Ошибка загрузки');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id, applyMeetingDetails]);

  useEffect(() => {
    if (!id || loading || !kzPollOnLoadRef.current) return;
    kzPollOnLoadRef.current = false;
    void pollKazakhRetranscribe();
  }, [id, loading, pollKazakhRetranscribe]);

  useEffect(() => {
    if (!id || !segments.length || insights) return;
    let cancelled = false;
    setInsightsLoading(true);
    apiService.getMeetingInsights(id)
      .then(data => { if (!cancelled) setInsights(data); })
      .catch((e) => {
        if (!cancelled && e instanceof Error && e.message.includes('404')) {
          toast.info('AI-анализ недоступен — перезапустите backend или нажмите «Обновить анализ»');
        }
      })
      .finally(() => { if (!cancelled) setInsightsLoading(false); });
    return () => { cancelled = true; };
  }, [id, segments.length, insights]);

  const handleRefreshInsights = useCallback(async () => {
    if (!id || insightsLoading) return;
    setInsightsLoading(true);
    try {
      const data = await apiService.refreshMeetingInsights(id);
      setInsights(data);
      const hasTopics = (data.key_topics?.length ?? 0) > 0;
      const hasMoments = (data.key_moments?.length ?? 0) > 0;
      const hasActions = (data.action_items?.length ?? 0) > 0;
      const hasSpeakerTones = (data.speaker_tones?.length ?? 0) > 0;
      if (hasTopics || hasMoments || hasActions || hasSpeakerTones || (data.tone_segments?.length ?? 0) > 1) {
        toast.success('Анализ обновлён');
      } else if ((data.speaker_tones?.length ?? 0) > 0) {
        toast.success('Анализ обновлён (частично — по эвристике реплик)');
      } else {
        toast.warning('Анализ завершён, но данных мало — попробуйте ещё раз');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка анализа');
    } finally {
      setInsightsLoading(false);
    }
  }, [id, insightsLoading]);

  const handleAnalyzeSlidesVlm = useCallback(async () => {
    if (!id || slidesVlmAnalyzing) return;
    setSlidesVlmAnalyzing(true);
    try {
      await apiService.analyzeMeetingSlidesVlm(id);
      toast.info('VLM-анализ слайдов запущен (5–15 мин)');
    } catch (e) {
      setSlidesVlmAnalyzing(false);
      toast.error(e instanceof Error ? e.message : 'Не удалось запустить анализ');
    }
  }, [id, slidesVlmAnalyzing]);

  useEffect(() => {
    if (!id || !slidesVlmAnalyzing) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const status = await apiService.getMeetingSlidesVlmStatus(id);
        if (cancelled) return;
        if (status.status === 'done') {
          setSlidesVlmAnalyzing(false);
          await refreshMeetingData();
          toast.success(`Слайды готовы${status.frames ? `: ${status.frames} кадров` : ''}`);
        } else if (status.status === 'error') {
          setSlidesVlmAnalyzing(false);
          toast.error(status.message || 'Ошибка VLM-анализа');
        }
      } catch {
        // ignore transient poll errors
      }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id, slidesVlmAnalyzing, refreshMeetingData]);

  const handleSummarizeSlides = useCallback(async () => {
    if (!id || summarizingSlides) return;
    setSummarizingSlides(true);
    try {
      const data = await apiService.summarizeMeetingSlides(id);
      setSlideTimeline(data.slide_timeline || []);
      setPresentationSlides((data as { presentation_slides?: SlideTimelineEntry[] }).presentation_slides || []);
      setSlideDeckSummary(data.slide_deck_summary || null);
      toast.success('Суммаризация слайдов готова');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка суммаризации');
    } finally {
      setSummarizingSlides(false);
    }
  }, [id, summarizingSlides]);

  const handleGeneratePptx = useCallback(async () => {
    if (!id || generatingPptx) return;
    setGeneratingPptx(true);
    try {
      if (!slideDeckSummary?.deck_summary) {
        const data = await apiService.summarizeMeetingSlides(id);
        setSlideTimeline(data.slide_timeline || []);
        setPresentationSlides((data as { presentation_slides?: SlideTimelineEntry[] }).presentation_slides || []);
        setSlideDeckSummary(data.slide_deck_summary || null);
      }
      await apiService.downloadMeetingSlidesPptx(id, name);
      toast.success('PPTX скачан');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка генерации PPTX');
    } finally {
      setGeneratingPptx(false);
    }
  }, [id, generatingPptx, slideDeckSummary, name]);

  const handleOpenThemeReplicas = useCallback((theme: KeywordTheme) => {
    const terms = theme.keywords.map(k => k.word).filter(Boolean);
    setTranscriptSearchTerms(terms);
    setTranscriptSearchQuery(terms.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(', '));
    setTab('transcription');
  }, []);

  const handleOpenKeywordReplicas = useCallback((word: string) => {
    setTranscriptSearchTerms([word]);
    setTranscriptSearchQuery(word.charAt(0).toUpperCase() + word.slice(1));
    setTab('transcription');
  }, []);

  const visibleTabs = useMemo((): FileTab[] => {
    if (videoScreenInsights) return [...baseTabs, 'video_insights'];
    return [...baseTabs];
  }, [videoScreenInsights]);

  const transcriptionFromSegments = useMemo(() => {
    if (!segments.length) return '';
    return segments
      .filter(seg => seg.text?.trim())
      .map(seg => {
        const speaker = getSpeakerName(seg);
        return `[${seg.start_time}] ${speaker}: ${seg.text.trim()}`;
      })
      .join('\n\n');
  }, [segments]);

  const recordingDurationSec = useMemo(() => {
    const fromDuration = parseTimeToSeconds(duration);
    const fromSegments = segments.reduce(
      (max, seg) => Math.max(max, getSegmentEndSec(seg)),
      0,
    );
    return Math.max(fromDuration, fromSegments, 1);
  }, [duration, segments]);

  const speakerTimelines = useMemo(() => {
    if (!segments.length) return [];

    const bySpeaker: Record<string, {
      name: string;
      label: string;
      segments: { startPct: number; endPct: number; startSec: number; endSec: number }[];
      totalSpeakingSec: number;
    }> = {};

    segments.forEach(seg => {
      const speakerName = getSpeakerName(seg);
      const speakerLabel = seg.speaker_label || speakerName;
      const startSec = getSegmentStartSec(seg);
      const endSec = getSegmentEndSec(seg);
      if (!bySpeaker[speakerName]) {
        bySpeaker[speakerName] = { name: speakerName, label: speakerLabel, segments: [], totalSpeakingSec: 0 };
      }
      bySpeaker[speakerName].segments.push({
        startPct: (startSec / recordingDurationSec) * 100,
        endPct: (endSec / recordingDurationSec) * 100,
        startSec,
        endSec,
      });
      bySpeaker[speakerName].totalSpeakingSec += Math.max(0, endSec - startSec);
    });

    const speakers = Object.values(bySpeaker);
    speakers.sort((a, b) => {
      const ra = resolveSpeakerRole(a.name).variant;
      const rb = resolveSpeakerRole(b.name).variant;
      const orderDiff = ROLE_SORT_ORDER[ra] - ROLE_SORT_ORDER[rb];
      if (orderDiff !== 0) return orderDiff;
      return b.totalSpeakingSec - a.totalSpeakingSec;
    });

    return speakers.map((speaker, index) => {
      const lang = resolveSpeakerLang(speaker.label, speaker.name, speakerLanguages)
        || segments.find(s => speakerNamesMatch(getSpeakerName(s), speaker.name))?.speaker_language;
      return {
        id: index + 1,
        name: speaker.name,
        label: speaker.label,
        language: lang,
        color: SPEAKER_COLORS[index % SPEAKER_COLORS.length],
        segments: speaker.segments,
        totalSpeakingSec: speaker.totalSpeakingSec,
      };
    });
  }, [segments, recordingDurationSec, speakerLanguages]);

  const totalSpeakingSec = useMemo(
    () => sumSegmentDurationsSec(segments),
    [segments],
  );

  const unionSpeakingSec = useMemo(
    () => unionSpeechDurationSec(segments),
    [segments],
  );

  const statsTotalSec = audioDurationSec || recordingDurationSec;

  const speechCoverage = useMemo(() => {
    const covered = unionSpeakingSec || totalSpeakingSec;
    const pct = statsTotalSec > 0 ? Math.round((covered / statsTotalSec) * 100) : 0;
    return { coveredSec: covered, coveragePct: pct, isLow: pct < 40 };
  }, [unionSpeakingSec, totalSpeakingSec, statsTotalSec]);

  const pauseStats = useMemo(() => {
    const covered = unionSpeakingSec || totalSpeakingSec;
    const unmarkedSec = Math.max(0, statsTotalSec - covered);
    const unmarkedPct = statsTotalSec > 0 ? Math.round((unmarkedSec / statsTotalSec) * 100) : 0;
    return { unmarkedSec, unmarkedPct, coveredSec: covered };
  }, [statsTotalSec, totalSpeakingSec, unionSpeakingSec]);

  const speakingStats = useMemo(() => {
    return speakerTimelines.map(speaker => ({
      name: speaker.name,
      displayName: formatSpeakerShortName(speaker.name),
      fullName: formatSpeakerFullName(speaker.name),
      language: speaker.language,
      color: speaker.color,
      seconds: speaker.totalSpeakingSec,
      /** Доля от размеченной речи (для donut) */
      percentOfSpeech: totalSpeakingSec > 0
        ? Math.round((speaker.totalSpeakingSec / totalSpeakingSec) * 100)
        : 0,
      /** Доля от всей записи — честная метрика для таймлайна */
      percentOfRecording: statsTotalSec > 0
        ? Math.round((speaker.totalSpeakingSec / statsTotalSec) * 100)
        : 0,
    }));
  }, [speakerTimelines, totalSpeakingSec, statsTotalSec]);

  const donutSlices = useMemo(
    () => speakingStats.map(s => ({
      label: s.fullName,
      color: s.color,
      percent: s.percentOfSpeech,
    })),
    [speakingStats],
  );

  const speakingPercentByName = useMemo(() => {
    const map: Record<string, number> = {};
    speakingStats.forEach(s => { map[s.name] = s.percentOfRecording; });
    return map;
  }, [speakingStats]);

  const participantsCount = useMemo(() => {
    const names = new Set<string>();
    segments.forEach(seg => {
      const n = getSpeakerName(seg);
      if (n) names.add(n);
    });
    return names.size;
  }, [segments]);

  const chatSpeakerNames = useMemo(() => {
    const byDuration = new Map<string, number>();
    segments.forEach(seg => {
      const name = getSpeakerName(seg);
      if (!name || /^SPEAKER_/i.test(name)) return;
      const dur = Math.max(0, getSegmentEndSec(seg) - getSegmentStartSec(seg));
      byDuration.set(name, (byDuration.get(name) || 0) + dur);
    });
    return Array.from(byDuration.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [segments]);

  const speakerColors = useMemo(() => {
    const map: Record<string, string> = {};
    speakerTimelines.forEach(s => {
      map[s.name] = s.color;
      if (s.label) map[s.label] = s.color;
    });
    segments.forEach(seg => {
      const key = seg.speaker_label?.match(/SPEAKER_\d+/i)?.[0]?.toUpperCase();
      const display = getSpeakerName(seg);
      if (key && display && map[display]) map[key] = map[display];
    });
    return map;
  }, [speakerTimelines, segments]);

  const handleExitSolo = useCallback(() => {
    setSoloSpeakerName(null);
  }, []);

  const handleSeekTime = useCallback((seconds: number) => {
    setSoloSpeakerName(null);
    setCurrentAudioTime(seconds);
    seekAudioRef.current?.(seconds);
  }, []);

  const handleSpeakerPlay = useCallback((speakerName: string) => {
    setSoloSpeakerName(prev => (prev && speakerNamesMatch(prev, speakerName) ? null : speakerName));
    const first = segments.find(seg => speakerNamesMatch(getSpeakerName(seg), speakerName));
    if (first) {
      handleSeekTime(getSegmentStartSec(first));
    }
  }, [segments, handleSeekTime]);

  const handleTogglePlayPause = useCallback(() => {
    togglePlayPauseRef.current?.();
  }, []);

  const handleAudioTimeChange = useCallback((time: number) => {
    setCurrentAudioTime(time);
  }, []);

  const handleAudioDurationChange = useCallback((dur: number) => {
    setAudioDurationSec(dur);
  }, []);

  const handleVideoPlayingChange = useCallback((playing: boolean) => {
    setIsVideoPlaying(playing);
  }, []);

  const hasTranscriptMedia = !!audioUrl || (isVideoMeeting && !!videoUrl);

  const protocolProgressHandler = useCallback((msg: string, extra?: { protocol?: string }) => {
    setRegenMessage(msg);
    if (extra?.protocol) {
      setProtocol(extra.protocol);
    }
  }, []);

  const watchActiveProtocolGeneration = useCallback(async () => {
    if (!id) return;
    try {
      const res = await apiService.watchProtocolRegeneration(id, protocolProgressHandler);
      setProtocol(res.protocol || '');
      const details = await apiService.getMeetingDetails(id);
      setProtocol(details.protocol || details.transcript || res.protocol || '');
      setSummary(details.summary || '');
      setSummaryStructured(details.summary_structured || null);
      setStructuredTasks(details.structured_tasks || []);
      const speeches = details.protocol_speeches_count;
      toast.success(
        speeches
          ? `Протокол обновлён: ${speeches} выступлений`
          : 'Протокол и конспект обновлены',
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Ошибка генерации протокола';
      if (!message.includes('не запущена')) {
        toast.error(message);
      }
    } finally {
      setRegenerating(false);
      setRegenMessage('');
    }
  }, [id, protocolProgressHandler]);

  useEffect(() => {
    if (!id || loading || !protocolRegenOnLoadRef.current) return;
    protocolRegenOnLoadRef.current = false;
    void watchActiveProtocolGeneration();
  }, [id, loading, watchActiveProtocolGeneration]);

  const handleRegenerateSummary = async () => {
    if (!id || regeneratingSummary) return;
    setRegeneratingSummary(true);
    setError('');
    try {
      toast.info('Генерация конспекта…');
      await apiService.generateMeetingSummary(id);
      const details = await apiService.getMeetingDetails(id);
      setSummary(details.summary || '');
      setSummaryStructured(details.summary_structured || null);
      setStructuredTasks(details.structured_tasks || []);
      toast.success('Конспект обновлён');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Ошибка генерации конспекта';
      setError(message);
      toast.error(message);
    } finally {
      setRegeneratingSummary(false);
    }
  };

  const handleRegenerateProtocol = async () => {
    if (!id || regenerating) return;
    setRegenerating(true);
    setRegenMessage('Запуск...');
    setError('');
    try {
      toast.info('Генерация протокола… AI-пересказ 11 выступлений (5–10 мин), затем повестка и задачи.');
      const res = await apiService.generateMeetingProtocol(id, protocolProgressHandler);
      setProtocol(res.protocol || '');
      const details = await apiService.getMeetingDetails(id);
      setProtocol(details.protocol || details.transcript || res.protocol || '');
      setSummary(details.summary || '');
      setSummaryStructured(details.summary_structured || null);
      setStructuredTasks(details.structured_tasks || []);
      const speeches = details.protocol_speeches_count;
      toast.success(
        speeches
          ? `Протокол обновлён: ${speeches} выступлений`
          : 'Протокол и конспект обновлены',
      );
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Ошибка генерации протокола';
      setError(message);
      toast.error(message);
    } finally {
      setRegenerating(false);
      setRegenMessage('');
    }
  };

  const handleSaveSpeakerLanguages = async (
    langs: Record<string, string>,
    genders: Record<string, string> = {},
  ) => {
    if (!id) return;
    setSavingSpeakerLangs(true);
    try {
      const res = await apiService.updateSpeakerSettings(id, langs, genders);
      setSpeakerLanguages(res.speaker_languages || langs);
      if (res.speaker_genders) setSpeakerGenders(res.speaker_genders);
      toast.success('Настройки спикеров сохранены');
      const segsResponse = await apiService.getMeetingSegments(id);
      applySegmentsResponse(segsResponse);
      if (res.auto_retranscribe || segsResponse.kazakh_retranscribe_pending) {
        void pollKazakhRetranscribe();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка сохранения');
    } finally {
      setSavingSpeakerLangs(false);
    }
  };

  const handleAutoDetectLanguages = async () => {
    if (!id) return;
    setDetectingLangs(true);
    try {
      const res = await apiService.detectSpeakerLanguages(id);
      const langs = res.speaker_languages || res.detected || {};
      setSpeakerLanguages(langs);
      const segsResponse = await apiService.getMeetingSegments(id);
      applySegmentsResponse(segsResponse);
      const hasKz = Object.values(langs).includes('kz');
      if (hasKz) {
        toast.success('Языки определены. Казахоязычные спикеры → перетранскрибация на kk');
      } else {
        toast.success('Языки спикеров определены автоматически');
      }
      if (res.auto_retranscribe || segsResponse.kazakh_retranscribe_pending) {
        void pollKazakhRetranscribe();
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка определения языков');
    } finally {
      setDetectingLangs(false);
    }
  };

  useEffect(() => {
    if (!id || !segments.length || autoDetectRanRef.current) return;
    if (Object.keys(speakerLanguages).length > 0) return;
    autoDetectRanRef.current = true;
    handleAutoDetectLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, segments.length]);

  const handleImportTeams = async (transcript: string) => {
    if (!id) throw new Error('Нет id встречи');
    setImportingTeams(true);
    try {
      const res = await apiService.importTeamsTranscript(id, transcript);
      setSpeakerLanguages(res.speaker_languages || {});
      const segsResponse = await apiService.getMeetingSegments(id);
      applySegmentsResponse(segsResponse);
      toast.success('Транскрипт Teams импортирован: имена и языки спикеров применены');
      if (res.auto_retranscribe || segsResponse.kazakh_retranscribe_pending) {
        void pollKazakhRetranscribe();
      }
      return res;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка импорта Teams');
      throw e;
    } finally {
      setImportingTeams(false);
    }
  };

  const handleRetranscribe = async () => {
    if (!id) return;
    setRetranscribing(true);
    try {
      await apiService.retranscribeMeeting(id);
      await pollKazakhRetranscribe();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка перетранскрибации');
      setRetranscribing(false);
    }
  };

  const reloadMeetingFromServer = useCallback(async () => {
    if (!id) return;
    const [details, segsResponse] = await Promise.all([
      apiService.getMeetingDetails(id),
      apiService.getMeetingSegments(id),
    ]);
    setName(details.name);
    setDuration(details.duration || '0:00:00');
    setProtocol(details.protocol || details.transcript || '');
    setSummary(details.summary || '');
    setSummaryStructured(details.summary_structured || null);
    setStructuredTasks(details.structured_tasks || []);
    setTranscription(details.transcription || '');
    setVideoScreenInsights(details.video_screen_insights || null);
    applySegmentsResponse(segsResponse);
    return { details, segsResponse };
  }, [id, applySegmentsResponse]);

  const handleRetranscribeAndProtocol = async () => {
    if (!id || retranscribeWithProtocol || rediarizing || regenerating) return;
    setRetranscribeWithProtocol(true);
    setRetranscribing(true);
    try {
      await apiService.retranscribeMeeting(id);
      await pollKazakhRetranscribe();
      await reloadMeetingFromServer();
      toast.info('Обновляем протокол по новой транскрипции…');
      setRegenerating(true);
      setRegenMessage('Протокол…');
      const res = await apiService.generateMeetingProtocol(id, setRegenMessage);
      setProtocol(res.protocol || '');
      const details = await apiService.getMeetingDetails(id);
      setSummary(details.summary || '');
      setSummaryStructured(details.summary_structured || null);
      setStructuredTasks(details.structured_tasks || []);
      toast.success('Транскрипция и протокол обновлены');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка ретранскрибации и протокола');
    } finally {
      setRetranscribeWithProtocol(false);
      setRetranscribing(false);
      setRegenerating(false);
      setRegenMessage('');
    }
  };

  const handleRediarize = async () => {
    if (!id || rediarizing) return;
    if (!window.confirm(
      'Полное улучшение: заново определить спикеров (Pyannote + голосовая база), перетранскрибировать сегменты и пересоздать протокол. Займёт 10–20 минут.',
    )) return;
    setRediarizing(true);
    const progressToast = toast.info('Диаризация, спикеры и протокол…', { autoClose: false });
    try {
      await apiService.rediarizeMeeting(id, (p) => {
        if (p.message) {
          toast.update(progressToast, { render: `${p.message} (${p.progress}%)` });
        }
      });
      const result = await reloadMeetingFromServer();
      toast.dismiss(progressToast);
      toast.success('Спикеры, транскрипция и протокол обновлены');
      if (result?.segsResponse.kazakh_retranscribe_pending) {
        void pollKazakhRetranscribe();
      }
    } catch (e) {
      toast.dismiss(progressToast);
      toast.error(e instanceof Error ? e.message : 'Ошибка полного улучшения');
    } finally {
      setRediarizing(false);
    }
  };

  const handleReprocessDeepgram = async () => {
    if (!id || reprocessingDeepgram) return;
    if (!window.confirm(
      'Переобработать встречу с подсказкой min/max спикеров из backend/.env? '
      + 'Транскрипт и протокол будут пересозданы (~5–10 мин).',
    )) return;
    setReprocessingDeepgram(true);
    const progressToast = toast.info('Транскрипция и диаризация…', { autoClose: false });
    try {
      await apiService.reprocessDeepgramMeeting(id, (p) => {
        if (p.message) {
          toast.update(progressToast, { render: `${p.message} (${p.progress}%)` });
        }
      });
      await reloadMeetingFromServer();
      toast.dismiss(progressToast);
      toast.success('Транскрипция и протокол обновлены');
    } catch (e) {
      toast.dismiss(progressToast);
      toast.error(e instanceof Error ? e.message : 'Ошибка переобработки');
    } finally {
      setReprocessingDeepgram(false);
    }
  };

  const handleFillGaps = async () => {
    if (!id || fillingGaps) return;
    setFillingGaps(true);
    try {
      const res = await apiService.fillMeetingGaps(id);
      toast.info(res.message || 'Дозаполнение пропусков запущено…');
      window.setTimeout(async () => {
        try {
          const segsResponse = await apiService.getMeetingSegments(id);
          applySegmentsResponse(segsResponse);
          toast.success('Сегменты обновлены — проверьте покрытие на таймлайне');
        } catch {
          toast.info('Обновите страницу через 2–5 мин, когда обработка завершится');
        } finally {
          setFillingGaps(false);
        }
      }, 120000);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Ошибка дозаполнения');
      setFillingGaps(false);
    }
  };

  const transcriptionFallback = useMemo(() => {
    if (segments.length) return '';
    if (transcription && transcription !== summary) return transcription;
    return '';
  }, [segments.length, transcription, summary]);

  const transcriptionImproveStats = useMemo(
    () => computeTranscriptionImproveStats(segments),
    [segments],
  );

  const transcriptionImproveMode = useMemo(
    () => getTranscriptionImproveMode(meetingPipeline),
    [meetingPipeline],
  );

  const transcriptionImproveBusy = rediarizing || reprocessingDeepgram || retranscribeWithProtocol || regenerating || retranscribing;

  useEffect(() => {
    setSectionEditing(false);
  }, [tab]);

  const handleStartSectionEdit = useCallback(() => {
    if (tab === 'protocol') {
      setEditDraftProtocol(protocol);
    } else if (tab === 'summary') {
      setEditDraftSummary(summary);
    } else if (tab === 'tasks') {
      const current = resolveMeetingTasks(protocol, structuredTasks, summaryStructured);
      setEditDraftTasks(
        current.length
          ? current.map(t => ({ ...t }))
          : [{ task: '', assignee: '—', deadline: DEFAULT_TASK_DEADLINE, priority: 'Средний' }],
      );
    }
    setSectionEditing(true);
  }, [tab, protocol, summary, structuredTasks, summaryStructured]);

  const handleCancelSectionEdit = useCallback(() => {
    setSectionEditing(false);
  }, []);

  const handleSaveSectionEdit = useCallback(async () => {
    if (!id || savingSection) return;
    setSavingSection(true);
    try {
      if (tab === 'protocol') {
        await apiService.saveMeetingToDb(id, { protocol: editDraftProtocol });
        setProtocol(editDraftProtocol);
      } else if (tab === 'summary') {
        await apiService.saveMeetingToDb(id, {
          summary: editDraftSummary,
          summary_structured: summaryStructured,
        });
        setSummary(editDraftSummary);
      } else if (tab === 'tasks') {
        const nextTasks = editDraftTasks
          .map(t => ({
            ...t,
            task: t.task.trim(),
            assignee: t.assignee?.trim() || '—',
            deadline: formatTaskDeadline(t.deadline?.trim()),
            priority: t.priority?.trim() || 'Средний',
          }))
          .filter(t => t.task);
        await apiService.saveMeetingToDb(id, { structured_tasks: nextTasks });
        setStructuredTasks(nextTasks);
      }
      setSectionEditing(false);
      toast.success(t('file.edit-saved'));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('file.edit-error'));
    } finally {
      setSavingSection(false);
    }
  }, [
    id,
    savingSection,
    tab,
    editDraftProtocol,
    editDraftSummary,
    editDraftTasks,
    summaryStructured,
    t,
  ]);

  if (!id) return null;

  if (loading) {
    return (
      <div className={cn()}>
        <div className={cn('go-back')} onClick={goBack}>
          <Chevron />
          <span>{t('action.back')}</span>
        </div>
        <p style={{ padding: '2rem' }}>Загрузка встречи...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn()}>
        <div className={cn('go-back')} onClick={goBack}>
          <Chevron />
          <span>{t('action.back')}</span>
        </div>
        <p style={{ padding: '2rem', color: '#c00' }}>{error}</p>
      </div>
    );
  }

  const exportDocName = (docType: 'protocol' | 'summary' | 'tasks' | 'transcription' | 'video_insights') =>
    buildExportFilename(name, id || '', meetingDate, docType, 'doc');

  const resolveExportTasks = (): StructuredTask[] => {
    if (structuredTasks.length) {
      return structuredTasks.map(t => ({
        task: t.task,
        assignee: t.assignee || '—',
        deadline: formatTaskDeadline(t.deadline),
        priority: t.priority || 'Средний',
      }));
    }
    if (protocol?.trim()) {
      return parseProtocolMarkdown(protocol).tasks.map(t => ({
        task: t.task,
        assignee: t.assignee || '—',
        deadline: formatTaskDeadline(t.deadline),
        priority: t.priority || 'Средний',
      }));
    }
    return [];
  };

  const handleDownload = () => {
    if (tab === 'protocol' && protocol) {
      downloadAsDoc(
        exportDocName('protocol'),
        `Протокол — ${name}`,
        markdownToHtml(sanitizeExportModelBranding(protocol)),
      );
      return;
    }
    if (tab === 'summary' && summary) {
      downloadAsDoc(
        exportDocName('summary'),
        `Конспект — ${name}`,
        summaryToHtml(summary),
      );
      return;
    }
    if (tab === 'tasks') {
      const tasks = resolveExportTasks();
      if (tasks.length) {
        downloadAsDoc(
          exportDocName('tasks'),
          `Поручения — ${name}`,
          tasksToHtml(tasks),
        );
      }
      return;
    }
    if (tab === 'transcription') {
      if (segments.length) {
        downloadAsDoc(
          exportDocName('transcription'),
          `Транскрипция — ${name}`,
          segmentsToHtml(segments.map(s => ({
            start_time: s.start_time,
            speaker: getSpeakerName(s),
            text: s.text,
          }))),
        );
      } else if (transcription) {
        downloadAsDoc(
          exportDocName('transcription'),
          `Транскрипция — ${name}`,
          `<pre>${transcription.replace(/</g, '&lt;')}</pre>`,
        );
      }
      return;
    }
    if (tab === 'video_insights' && videoScreenInsights) {
      downloadAsDoc(
        exportDocName('video_insights'),
        `Инсайты (Видео) — ${name}`,
        videoScreenInsightsToHtml(videoScreenInsights, name),
      );
    }
  };

  const canDownload =
    (tab === 'protocol' && !!protocol) ||
    (tab === 'summary' && !!summary) ||
    (tab === 'tasks' && resolveExportTasks().length > 0) ||
    (tab === 'transcription' && (segments.length > 0 || !!transcription)) ||
    (tab === 'video_insights' && !!videoScreenInsights);

  const canDownloadPdf =
    (tab === 'protocol' && !!protocol) ||
    (tab === 'summary' && (!!summary || !!summaryStructured)) ||
    (tab === 'tasks' && resolveExportTasks().length > 0) ||
    (tab === 'transcription' && (segments.length > 0 || !!transcription)) ||
    (tab === 'video_insights' && !!videoScreenInsights);

  const handleDownloadPdf = async (doc?: 'protocol' | 'summary' | 'transcription' | 'stats' | 'tasks' | 'video_insights' | 'all') => {
    if (!id || pdfLoading) return;
    const docType = doc || (
      tab === 'protocol' ? 'protocol'
        : tab === 'summary' ? 'summary'
          : tab === 'tasks' ? 'tasks'
            : tab === 'video_insights' ? 'video_insights'
              : 'transcription'
    );
    setPdfLoading(true);
    setError('');
    try {
      const { blob, filename } = await apiService.downloadMeetingPdf(id, docType);
      downloadBlob(filename, blob);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка скачивания PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadInsightsPptx = async () => {
    if (!id || insightsPptxLoading || !videoScreenInsights) return;
    setInsightsPptxLoading(true);
    setError('');
    try {
      const { blob, filename } = await apiService.downloadMeetingInsightsPptx(id);
      downloadBlob(filename, blob);
      toast.success('PPTX скачан');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка скачивания PPTX');
    } finally {
      setInsightsPptxLoading(false);
    }
  };

  const handleSaveToDb = async () => {
    if (!id || savingToDb) return;
    setSavingToDb(true);
    try {
      const transcriptionText = transcriptionFromSegments || transcription || '';
      const result = await apiService.saveMeetingToDb(id, {
        protocol,
        summary,
        summary_structured: summaryStructured,
        transcription: transcriptionText,
        structured_tasks: structuredTasks,
      });
      toast(
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <GreenTick />
          {result.message || t('file.save-db-success')}
        </div>,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('file.save-db-error'));
    } finally {
      setSavingToDb(false);
    }
  };

  const handleDownloadAll = () => {
    if (protocol) {
      downloadAsDoc(
        exportDocName('protocol'),
        `Протокол — ${name}`,
        markdownToHtml(sanitizeExportModelBranding(protocol)),
      );
    }
    if (summary) {
      downloadAsDoc(exportDocName('summary'), `Конспект — ${name}`, summaryToHtml(summary));
    }
    const allTasks = resolveExportTasks();
    if (allTasks.length) {
      downloadAsDoc(exportDocName('tasks'), `Поручения — ${name}`, tasksToHtml(allTasks));
    }
    if (segments.length) {
      downloadAsDoc(
        exportDocName('transcription'),
        `Транскрипция — ${name}`,
        segmentsToHtml(segments.map(s => ({
          start_time: s.start_time,
          speaker: getSpeakerName(s),
          text: s.text,
        }))),
      );
    } else if (transcription) {
      downloadAsDoc(
        exportDocName('transcription'),
        `Транскрипция — ${name}`,
        `<pre>${transcription.replace(/</g, '&lt;')}</pre>`,
      );
    }
    if (videoScreenInsights) {
      downloadAsDoc(
        exportDocName('video_insights'),
        `Инсайты (Видео) — ${name}`,
        videoScreenInsightsToHtml(videoScreenInsights, name),
      );
    }
  };

  return (
    <div className={cn()}>
      <div className={cn('go-back')} onClick={goBack}>
        <Chevron />
        <span>{t('action.back')}</span>
      </div>

      <div className={cn('heading-row')}>
        <div className={cn('heading')}>{name}</div>
        <button
          type="button"
          className={cn('share-btn')}
          onClick={() => setShareModalOpen(true)}
        >
          <Users />
          {t('file.share')}
        </button>
      </div>

      {meetingStatus === 'processing' && !sectionEditing && (
        <ProcessingPanel progress={liveProcessing} fallbackMessage={liveProgress?.message} />
      )}

      {isLiveSyncing && meetingStatus !== 'processing' && !sectionEditing && (
        <div className={cn('protocol-regen-banner', { sync: true })}>
          <span className={cn('sync-pulse')} aria-hidden />
          {t('file.auto-sync-processing')}
          <span className={cn('sync-badge')}>{t('file.auto-sync-live')}</span>
        </div>
      )}

      <div className={cn('content')}>
        <div className={cn('text')}>
          <div className={cn('tabs-row')}>
            <div className={cn('tabs')}>
              {visibleTabs.map(item => (
                <div
                  key={item}
                  className={cn('tab', { active: tab === item })}
                  onClick={() => setTab(item)}
                >
                  {item === 'analytics'
                    ? t('file.analytics')
                    : item === 'video_insights'
                      ? t('file.video-insights')
                      : t(`file.${item}`)}
                </div>
              ))}
            </div>
            <div className={cn('download-actions')}>
              <button
                type="button"
                className={cn('download-btn', { secondary: true })}
                onClick={() => handleDownloadPdf('all')}
                disabled={pdfLoading || (!protocol && !summary && !resolveExportTasks().length && !segments.length && !transcription && !videoScreenInsights)}
              >
                {pdfLoading ? 'PDF…' : 'PDF всё'}
              </button>
              <button
                type="button"
                className={cn('download-btn', { secondary: true })}
                onClick={handleDownloadAll}
                disabled={!protocol && !summary && !resolveExportTasks().length && !segments.length && !transcription && !videoScreenInsights}
              >
                DOC всё
              </button>
              {tab === 'summary' && (
                <button
                  type="button"
                  className={cn('download-btn', { secondary: true })}
                  onClick={handleRegenerateSummary}
                  disabled={regeneratingSummary || !segments.length || sectionEditing}
                >
                  {regeneratingSummary ? 'Конспект…' : 'Обновить конспект'}
                </button>
              )}
              {tab === 'protocol' && (
                <button
                  type="button"
                  className={cn('download-btn', { secondary: true })}
                  onClick={handleRegenerateProtocol}
                  disabled={regenerating || sectionEditing}
                >
                  {regenerating ? (regenMessage || 'Генерация…') : 'Обновить AI'}
                </button>
              )}
              <button
                type="button"
                className={cn('download-btn', { secondary: true })}
                onClick={() => handleDownloadPdf()}
                disabled={!canDownloadPdf || pdfLoading}
              >
                {pdfLoading ? 'PDF…' : 'Скачать PDF'}
              </button>
              {tab === 'video_insights' && (
                <button
                  type="button"
                  className={cn('download-btn', { secondary: true })}
                  onClick={handleDownloadInsightsPptx}
                  disabled={!videoScreenInsights || insightsPptxLoading}
                >
                  {insightsPptxLoading ? 'PPTX…' : t('file.video-insights-pptx')}
                </button>
              )}
              <button
                type="button"
                className={cn('download-btn')}
                onClick={handleDownload}
                disabled={!canDownload}
              >
                Скачать DOC
              </button>
              <button
                type="button"
                className={cn('download-btn', { secondary: true, save: true })}
                onClick={handleSaveToDb}
                disabled={savingToDb}
              >
                {savingToDb ? t('file.save-db-saving') : t('file.save-db')}
              </button>
            </div>
          </div>

          <div className={cn('tab-content')}>
            {editableTabs.includes(tab) && (
              <MeetingSectionEditToolbar
                editing={sectionEditing}
                saving={savingSection}
                onEdit={handleStartSectionEdit}
                onSave={() => { void handleSaveSectionEdit(); }}
                onCancel={handleCancelSectionEdit}
              />
            )}
            {tab === 'protocol' && (
              <>
                {regenerating && !sectionEditing && (
                  <div className={cn('protocol-regen-banner')}>
                    {regenMessage || 'Генерация протокола…'}
                  </div>
                )}
                <ProtocolView
                  protocol={protocol}
                  meetingName={name}
                  duration={duration}
                  date={meetingDate}
                  participantsCount={participantsCount}
                  speakerColors={speakerColors}
                  editing={sectionEditing}
                  editValue={editDraftProtocol}
                  onEditChange={setEditDraftProtocol}
                />
              </>
            )}
            {tab === 'summary' && (
              <SummaryView
                summary={summary}
                structured={summaryStructured}
                meetingName={name}
                duration={duration}
                editing={sectionEditing}
                editValue={editDraftSummary}
                onEditChange={setEditDraftSummary}
              />
            )}
            {tab === 'tasks' && (
              <MeetingTasksPanel
                protocol={protocol}
                structuredTasks={structuredTasks}
                summaryStructured={summaryStructured}
                editing={sectionEditing}
                editTasks={editDraftTasks}
                onEditTasksChange={setEditDraftTasks}
              />
            )}
            {tab === 'transcription' && (
              <>
                <TeamsImportPanel onImport={handleImportTeams} importing={importingTeams} />
                <TranscriptionImprovePanel
                  stats={transcriptionImproveStats}
                  mode={transcriptionImproveMode}
                  busy={transcriptionImproveBusy}
                  fullImproveBusy={rediarizing}
                  reprocessDeepgramBusy={reprocessingDeepgram}
                  retranscribeBusy={retranscribeWithProtocol || (retranscribing && !rediarizing && !reprocessingDeepgram)}
                  protocolBusy={regenerating && !retranscribeWithProtocol && !rediarizing && !reprocessingDeepgram}
                  onFullImprove={() => { void handleRediarize(); }}
                  onRetranscribeAndProtocol={() => { void handleRetranscribeAndProtocol(); }}
                  onReprocessDeepgram={() => { void handleReprocessDeepgram(); }}
                  onRegenerateProtocol={() => { void handleRegenerateProtocol(); }}
                />
                <SpeakerLanguagePanel
                  segments={segments}
                  pipeline={meetingPipeline}
                  initialLanguages={speakerLanguages}
                  initialGenders={speakerGenders}
                  onSave={handleSaveSpeakerLanguages}
                  onRetranscribe={handleRetranscribe}
                  onAutoDetect={handleAutoDetectLanguages}
                  saving={savingSpeakerLangs}
                  retranscribing={retranscribing && !retranscribeWithProtocol}
                  detecting={detectingLangs}
                />
                {id && (
                  <TranscriptionMediaPanel
                    meetingId={id}
                    isVideoMeeting={isVideoMeeting}
                    audioUrl={audioUrl}
                    videoUrl={videoUrl}
                    segments={segments}
                    speakerTimelines={speakerTimelines}
                    durationSec={recordingDurationSec}
                    currentTime={currentAudioTime}
                    soloSpeakerName={soloSpeakerName}
                    insights={insights}
                    slideTimeline={slideTimeline}
                    presentationSlides={presentationSlides}
                    presentationDecks={presentationDecks}
                    slideDeckSummary={slideDeckSummary}
                    getFrameUrl={apiService.getMeetingFrameUrl}
                    onExitSolo={handleExitSolo}
                    onTimeChange={handleAudioTimeChange}
                    onDurationChange={handleAudioDurationChange}
                    onPlayingChange={handleVideoPlayingChange}
                    onSeek={handleSeekTime}
                    onSpeakerPlay={handleSpeakerPlay}
                    onVideoAttached={async () => {
                      if (!id) return;
                      const available = await apiService.checkMeetingVideoAvailable(id);
                      setVideoUrl(available ? apiService.getMeetingVideoUrl(id) : '');
                      setSlidesVlmAnalyzing(true);
                      void refreshMeetingData();
                    }}
                    slidesAnalyzing={slidesVlmAnalyzing}
                    onAnalyzeSlides={handleAnalyzeSlidesVlm}
                    seekRef={seekAudioRef}
                    togglePlayPauseRef={togglePlayPauseRef}
                  />
                )}
                <div className={cn('transcript-clean-toggle-row')}>
                  <button
                    type="button"
                    className={cn('download-btn', { secondary: true, active: cleanTranscript.enabled })}
                    onClick={() => { void cleanTranscript.toggle(); }}
                    disabled={cleanTranscript.loading}
                    aria-pressed={cleanTranscript.enabled}
                  >
                    {cleanTranscript.loading
                      ? t('voice-analytics.clean-loading')
                      : t('voice-analytics.clean-toggle')}
                  </button>
                  {cleanTranscript.error && (
                    <span className={cn('transcript-clean-toggle-error')}>{cleanTranscript.error}</span>
                  )}
                </div>
                <TranscriptionView
                  segments={cleanTranscript.segments}
                  fallbackText={transcriptionFallback}
                  duration={duration}
                  onSeekTime={handleSeekTime}
                  onSpeakerPlay={handleSpeakerPlay}
                  soloSpeakerName={soloSpeakerName}
                  externalSearch={transcriptSearchQuery}
                  externalSearchTerms={transcriptSearchTerms}
                  taggedReplicas={insights?.tagged_replicas}
                  onExternalSearchChange={(value) => {
                    setTranscriptSearchQuery(value);
                    if (!value.trim()) setTranscriptSearchTerms([]);
                  }}
                />
              </>
            )}
            {tab === 'analytics' && (
              <MeetingAnalyticsTab
                isVideoMeeting={isVideoMeeting}
                videoUrl={videoUrl}
                audioUrl={audioUrl}
                meetingDateLabel={meetingDate ? formatMeetingDate(meetingDate) : ''}
                segmentsCount={segments.length}
                recordingDurationSec={recordingDurationSec}
                onTimeChange={handleAudioTimeChange}
                onDurationChange={handleAudioDurationChange}
                onPlayingChange={handleVideoPlayingChange}
                seekRef={seekAudioRef}
                togglePlayPauseRef={togglePlayPauseRef}
                videoAnalysis={videoAnalysis}
                speakerTimelines={speakerTimelines}
                segments={segments}
                currentAudioTime={currentAudioTime}
                isVideoPlaying={isVideoPlaying}
                statsTotalSec={statsTotalSec}
                totalSpeakingSec={totalSpeakingSec}
                participantsCount={participantsCount}
                donutSlices={donutSlices}
                speakingPercentByName={speakingPercentByName}
                pauseStats={pauseStats}
                speechCoverage={speechCoverage}
                insights={insights}
                slideTimeline={slideTimeline}
                presentationSlides={presentationSlides}
                presentationDecks={presentationDecks}
                slideDeckSummary={slideDeckSummary}
                meetingId={id!}
                getFrameUrl={apiService.getMeetingFrameUrl}
                onSummarizeSlides={handleSummarizeSlides}
                onGeneratePptx={handleGeneratePptx}
                summarizingSlides={summarizingSlides}
                generatingPptx={generatingPptx}
                insightsLoading={insightsLoading}
                fillingGaps={fillingGaps}
                pdfLoading={pdfLoading}
                onSeek={handleSeekTime}
                onSpeakerPlay={handleSpeakerPlay}
                soloSpeakerName={soloSpeakerName}
                onExitSolo={handleExitSolo}
                onTogglePlayPause={handleTogglePlayPause}
                onFillGaps={handleFillGaps}
                onDownloadStatsPdf={() => void handleDownloadPdf('stats')}
                onRefreshInsights={handleRefreshInsights}
                onOpenThemeReplicas={handleOpenThemeReplicas}
                onOpenKeywordReplicas={handleOpenKeywordReplicas}
                conversationMetrics={conversationMetrics}
                fillerStats={fillerStats}
                emotionAnalysis={emotionAnalysis}
                prosodyAnalysis={prosodyAnalysis}
                recomputingAnalytics={recomputingAnalytics}
                onRecomputeAnalytics={() => { void handleRecomputeAnalytics(); }}
              />
            )}
            {tab === 'video_insights' && videoScreenInsights && (
              <MeetingVideoInsightsTab
                insights={videoScreenInsights}
                meetingName={name}
                actionItems={summaryStructured?.action_items || []}
              />
            )}
            <FileChat
              meetingId={id}
              section={tab}
              meetingName={name}
              hasProtocol={!!protocol}
              hasTranscription={segments.length > 0 || !!transcription}
              insights={insights}
              summaryStructured={summaryStructured}
              videoScreenInsights={videoScreenInsights}
              structuredTasks={structuredTasks}
              speakers={chatSpeakerNames}
            />
          </div>
        </div>
      </div>

      <ShareMeetingModal
        show={shareModalOpen}
        meetingId={id!}
        meetingName={name}
        onClose={() => setShareModalOpen(false)}
      />
    </div>
  )
}

export default FilePage;
